import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Users, DollarSign, AlertCircle, AlertTriangle } from 'lucide-react';

const ActivityEnrollModal = ({
  isOpen,
  onClose,
  onSubmit,
  activities,
  categories,
  students,
  enrollments,      // ✅ NEW: List of current enrollments
  selectedActivity,
  selectedYear
}) => {
  // Form state
  const [activityType, setActivityType] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [activityId, setActivityId] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [notes, setNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showIneligible, setShowIneligible] = useState(false);

  // Pre-fill if activity was selected
  useEffect(() => {
    if (selectedActivity) {
      setActivityId(selectedActivity.ActivityID.toString());
      setActivityType(selectedActivity.ActivityType);
      setCategoryId(selectedActivity.CategoryID.toString());
    }
  }, [selectedActivity]);

  // Filter categories by type
  const filteredCategories = useMemo(() => {
    if (!activityType) return categories;
    return categories.filter(c => c.ActivityType === activityType);
  }, [categories, activityType]);

  // Filter activities by category
  const filteredActivities = useMemo(() => {
    let result = activities;
    if (activityType) {
      result = result.filter(a => a.ActivityType === activityType);
    }
    if (categoryId) {
      result = result.filter(a => a.CategoryID === parseInt(categoryId));
    }
    return result;
  }, [activities, activityType, categoryId]);

  // Selected activity details
  const activity = useMemo(() => {
    if (!activityId) return null;
    return activities.find(a => a.ActivityID === parseInt(activityId));
  }, [activities, activityId]);

  // Get list of eligible classes for selected activity
  const eligibleClasses = useMemo(() => {
    if (!activity) return null; // null means all classes allowed
    if (activity.AllClasses) return null;
    
    // Parse ApplicableClasses string (e.g., "EB1, EB2, EB3")
    if (activity.ApplicableClasses && activity.ApplicableClasses !== 'All Classes') {
      return activity.ApplicableClasses.split(',').map(c => c.trim());
    }
    return null;
  }, [activity]);

  // Check if a student is eligible based on their class
  const isStudentEligible = (student) => {
    if (!eligibleClasses) return true; // All classes allowed
    
    // Get student's class (could be in Grade, Class, or ClassName field)
    const studentClass = student.Grade || student.Class || student.ClassName || '';
    
    // Check for exact match first
    if (eligibleClasses.includes(studentClass)) return true;
    
    // Check if student's class STARTS WITH any eligible class (handles sections like "S1S A" matching "S1S")
    // Also check if any eligible class STARTS WITH student's class (handles "S1S" matching "S1S")
    // Case-insensitive comparison as fallback
    return eligibleClasses.some(eligibleClass => 
      studentClass.startsWith(eligibleClass) || 
      eligibleClass.startsWith(studentClass) ||
      studentClass.toLowerCase() === eligibleClass.toLowerCase() ||
      studentClass.toLowerCase().startsWith(eligibleClass.toLowerCase()) ||
      eligibleClass.toLowerCase().startsWith(studentClass.toLowerCase())
    );
  };

  // ✅ NEW: Check if student is already enrolled in the selected activity
  const isStudentAlreadyEnrolled = (student) => {
    if (!activityId || !enrollments) return false;
    
    // Check if there's an enrollment for this student in this activity
    return enrollments.some(enrollment => 
      enrollment.StudentID === student.StudentID && 
      enrollment.ActivityID === parseInt(activityId) &&
      enrollment.Status === 'Active' // Only count active enrollments
    );
  };

  // Filter and sort students
  const { eligibleStudents, ineligibleStudents, alreadyEnrolledStudents } = useMemo(() => {
    let filtered = students;
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.FirstName?.toLowerCase().includes(query) ||
        s.LastName?.toLowerCase().includes(query) ||
        s.FatherName?.toLowerCase().includes(query) ||
        s.StudentID?.toLowerCase().includes(query) ||
        s.FamilyID?.toString().toLowerCase().includes(query) ||
        s.Grade?.toLowerCase().includes(query) ||
        s.Class?.toLowerCase().includes(query)
      );
    }
    
    // ✅ NEW: Filter out already enrolled students first
    const alreadyEnrolled = filtered.filter(s => isStudentAlreadyEnrolled(s));
    const notEnrolled = filtered.filter(s => !isStudentAlreadyEnrolled(s));
    
    // Separate eligible and ineligible from not enrolled students
    const eligible = notEnrolled.filter(s => isStudentEligible(s));
    const ineligible = notEnrolled.filter(s => !isStudentEligible(s));
    
    return { 
      eligibleStudents: eligible, 
      ineligibleStudents: ineligible,
      alreadyEnrolledStudents: alreadyEnrolled 
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students, searchQuery, eligibleClasses, activityId, enrollments]);

  // Calculate costs
  const pricing = useMemo(() => {
    if (!activity || selectedStudents.length === 0) {
      return { totalCost: 0, discount: 0, finalCost: 0 };
    }
    
    const pricePerStudent = activity.PriceUSD || 0;
    const discount = parseFloat(discountAmount) || 0;
    const totalCost = pricePerStudent * selectedStudents.length;
    const totalDiscount = discount * selectedStudents.length;
    
    return {
      pricePerStudent,
      totalCost,
      discount: totalDiscount,
      finalCost: totalCost - totalDiscount
    };
  }, [activity, selectedStudents, discountAmount]);

  // Toggle student selection
  const toggleStudent = (student) => {
    // Don't allow selecting ineligible students
    if (!isStudentEligible(student)) return;
    
    // ✅ NEW: Don't allow selecting already enrolled students
    if (isStudentAlreadyEnrolled(student)) return;
    
    setSelectedStudents(prev => {
      const exists = prev.find(s => s.StudentID === student.StudentID);
      if (exists) {
        return prev.filter(s => s.StudentID !== student.StudentID);
      }
      return [...prev, student];
    });
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!activityId || selectedStudents.length === 0) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // If single student, use regular enroll
      if (selectedStudents.length === 1) {
        await onSubmit({
          activityId: parseInt(activityId),
          studentId: selectedStudents[0].StudentID,
          familyId: selectedStudents[0].FamilyID,
          discountAmount: parseFloat(discountAmount) || 0,
          discountReason: discountReason || null,
          notes: notes || null
        });
      } else {
        // Bulk enroll
        await onSubmit({
          activityId: parseInt(activityId),
          studentIds: selectedStudents.map(s => s.StudentID),
          familyIds: selectedStudents.map(s => s.FamilyID),
          discountAmount: parseFloat(discountAmount) || 0,
          discountReason: discountReason || null,
          bulk: true
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">Enroll in Activity</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Activity Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity Type
                </label>
                <select
                  value={activityType}
                  onChange={(e) => {
                    setActivityType(e.target.value);
                    setCategoryId('');
                    setActivityId('');
                    setSelectedStudents([]);
                  }}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="AfterSchoolSport">Afterschool Sports</option>
                  <option value="SchoolActivity">School Activities</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setActivityId('');
                    setSelectedStudents([]);
                  }}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Category</option>
                  {filteredCategories.map(cat => (
                    <option key={cat.CategoryID} value={cat.CategoryID}>
                      {cat.CategoryName}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activity <span className="text-red-500">*</span>
                </label>
                <select
                  value={activityId}
                  onChange={(e) => {
                    setActivityId(e.target.value);
                    setSelectedStudents([]); // Clear selection when activity changes
                  }}
                  required
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Activity</option>
                  {filteredActivities.map(act => (
                    <option key={act.ActivityID} value={act.ActivityID}>
                      {act.ActivityName} - ${act.PriceUSD} ({act.PaymentSchedule})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Activity Details */}
            {activity && (
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900">{activity.ActivityName}</h3>
                    <p className="text-sm text-blue-700">
                      {activity.CategoryName} • {activity.PaymentSchedule} Payment
                    </p>
                    {activity.Description && (
                      <p className="text-sm text-blue-600 mt-1">{activity.Description}</p>
                    )}
                    {/* Show eligible classes */}
                    <p className="text-sm text-blue-700 mt-2">
                      <Users className="w-4 h-4 inline mr-1" />
                      Eligible Classes: <strong>{activity.ApplicableClasses || 'All Classes'}</strong>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-900">${activity.PriceUSD}</p>
                    {activity.PaymentSchedule === 'Trimester' && (
                      <p className="text-sm text-blue-700">
                        ${(activity.PriceUSD / 3).toFixed(2)}/trimester
                      </p>
                    )}
                  </div>
                </div>
                {activity.MaxCapacity && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700">
                      {activity.EnrolledCount || 0} / {activity.MaxCapacity} enrolled
                      {activity.SpotsRemaining !== null && activity.SpotsRemaining <= 5 && (
                        <span className="ml-2 text-orange-600 font-medium">
                          ({activity.SpotsRemaining} spots left!)
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Student Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Students <span className="text-red-500">*</span>
              </label>
              
              {/* Search */}
              <div className="relative mb-3">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, family, class, or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Selected students */}
              {selectedStudents.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {selectedStudents.map(student => (
                    <span
                      key={student.StudentID}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {student.FirstName} {student.LastName}
                      <span className="text-blue-600">({student.Grade || student.Class})</span>
                      <button
                        type="button"
                        onClick={() => toggleStudent(student)}
                        className="hover:text-blue-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              
              {/* Class restriction notice */}
              {eligibleClasses && (
                <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Class Restriction Active</p>
                    <p className="text-amber-700">
                      Only students in <strong>{eligibleClasses.join(', ')}</strong> can enroll in this activity.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Student list */}
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {eligibleStudents.length === 0 && ineligibleStudents.length === 0 && alreadyEnrolledStudents.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No students found</div>
                ) : eligibleStudents.length === 0 && alreadyEnrolledStudents.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    No eligible students found for this activity's class restrictions
                  </div>
                ) : (
                  <>
                    {/* ✅ NEW: Already Enrolled Students Notice */}
                    {alreadyEnrolledStudents.length > 0 && activityId && (
                      <div className="bg-blue-50 border-b-2 border-blue-200 px-4 py-2">
                        <div className="flex items-center gap-2 text-sm text-blue-800">
                          <AlertCircle className="w-4 h-4" />
                          <span className="font-medium">
                            {alreadyEnrolledStudents.length} student(s) already enrolled 
                            {searchQuery && ' (filtered by search)'}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Eligible Students */}
                    {eligibleStudents.map(student => {
                      const isSelected = selectedStudents.some(s => s.StudentID === student.StudentID);
                      return (
                        <div
                          key={student.StudentID}
                          onClick={() => toggleStudent(student)}
                          className={`px-4 py-2 cursor-pointer border-b last:border-0 flex items-center justify-between ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div>
                            <span className="font-medium">
                              {student.FirstName} {student.LastName}
                            </span>
                            <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-sm font-medium">
                              {student.Grade || student.Class}
                            </span>
                            <span className="text-gray-500 ml-2 text-sm">
                              {student.FatherName}
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            readOnly
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                        </div>
                      );
                    })}
                    
                    {/* Show ineligible students toggle */}
                    {ineligibleStudents.length > 0 && eligibleClasses && (
                      <div className="border-t">
                        <button
                          type="button"
                          onClick={() => setShowIneligible(!showIneligible)}
                          className="w-full px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 text-left"
                        >
                          {showIneligible ? '▼' : '▶'} {ineligibleStudents.length} students not eligible 
                          (wrong class)
                        </button>
                        {showIneligible && ineligibleStudents.map(student => (
                          <div
                            key={student.StudentID}
                            className="px-4 py-2 border-b last:border-0 flex items-center justify-between bg-gray-50 opacity-50"
                          >
                            <div>
                              <span className="text-gray-500">
                                {student.FirstName} {student.LastName}
                              </span>
                              <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded text-sm font-medium">
                                {student.Grade || student.Class}
                              </span>
                              <span className="text-gray-400 ml-2 text-sm">
                                Not eligible
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Discount */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Amount ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Per student</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Reason
                </label>
                <select
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select reason</option>
                  <option value="Sibling Discount">Sibling Discount</option>
                  <option value="Staff Discount">Staff Discount</option>
                  <option value="Financial Aid">Financial Aid</option>
                  <option value="Scholarship">Scholarship</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional notes..."
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Cost Summary */}
            {activity && selectedStudents.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Cost Summary
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Price per student</span>
                    <span>${pricing.pricePerStudent.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Number of students</span>
                    <span>× {selectedStudents.length}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Subtotal</span>
                    <span>${pricing.totalCost.toFixed(2)}</span>
                  </div>
                  {pricing.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-${pricing.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span>${pricing.finalCost.toFixed(2)}</span>
                  </div>
                  {activity.PaymentSchedule === 'Trimester' && (
                    <div className="text-gray-500 text-xs">
                      Billed as ${(pricing.finalCost / 3).toFixed(2)} per trimester
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Capacity Warning */}
            {activity && activity.MaxCapacity && 
             activity.SpotsRemaining !== null && 
             selectedStudents.length > activity.SpotsRemaining && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Not enough spots available</p>
                  <p className="text-sm text-red-700">
                    This activity only has {activity.SpotsRemaining} spot(s) remaining, 
                    but you're trying to enroll {selectedStudents.length} student(s).
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!activityId || selectedStudents.length === 0 || submitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Enrolling...' : `Enroll ${selectedStudents.length} Student${selectedStudents.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityEnrollModal;